import { atom, selector, selectorFamily } from "recoil";
import {
  JobActivityRequestTouched,
  JobActivity,
  JobActivityCore,
  JobTypeActivityRequest,
  JobTypeRequest,
  JobTypeRequestTouched,
} from "../types/jobs";
import {
  JOB_TYPE_DEFAULTS,
  JOB_TYPE_TOUCHED_DEFAULTS,
} from "../constants/jobSkills";
import { PaginatedResponse } from "../types/app";
import {
  ACTIVITY_DEFAULTS,
  ACTIVITY_TOUCHED_DEFAULTS,
} from "../constants/jobActivities";

export const editJobTypeState = atom<JobTypeRequest>({
  key: "app.jobs.types.edit",
  default: JOB_TYPE_DEFAULTS,
});

export const editJobTypeTouchedState = atom<JobTypeRequestTouched>({
  key: "app.jobs.types.edit.touched",
  default: JOB_TYPE_TOUCHED_DEFAULTS,
});

export const editJobTypeValidState = selector<{
  [key in keyof JobTypeRequest]: boolean;
}>({
  key: "app.jobs.types.edit.valid",
  get: ({ get }) => {
    const fields = get(editJobTypeState);

    // SET ALL FIELDS TO BE VALID
    const validFields = Object.fromEntries(
      Object.keys(fields).map((key) => [key, true])
    ) as {
      [key in keyof JobTypeRequest]: boolean;
    };

    // VALIDATE REQUIRED FIELDS
    if (!fields.name) {
      validFields.name = false;
    }
    if (!fields.avgDuration) {
      validFields.avgDuration = false;
    }
    if (!fields.cost) {
      validFields.cost = false;
    }
    if (fields.tenants.length === 0) {
      validFields.tenants = false;
    }

    return validFields;
  },
});

export const editJobTypeErrorState = selector({
  key: "app.jobs.types.edit.error",
  get: ({ get }) => {
    const validFields = get(editJobTypeValidState);
    return Object.values(validFields).some((valid) => !valid);
  },
});

export const updateJobTypeState = atom<string | undefined>({
  key: "app.jobs.types.update",
  default: undefined,
});

export const jobActivitiesState = atom<
  PaginatedResponse<JobActivityCore> | undefined
>({
  key: "app.jobs.types.activities",
  default: undefined,
});

export const jobActivityDetailState = atom<JobActivity | undefined>({
  key: "app.job.type.activity.detail",
  default: undefined,
});

export const jobActivitiesIdsState = selector<string[] | undefined>({
  key: "app.jobs.types.activities.ids",
  get: ({ get }) => {
    const jobs = get(jobActivitiesState);
    if (!jobs) return;
    return jobs.items.map((job) => job._id);
  },
});

export const selectedJobActivityIdState = atom<string | undefined>({
  key: "app.job.type.activity.selected",
  default: undefined,
});

export const jobActivityState = selectorFamily<
  JobActivityCore | undefined,
  string
>({
  key: "app.job.type.activity",
  get:
    (id) =>
    ({ get }) => {
      const activities = get(jobActivitiesState);
      return activities?.items.find((activity) => activity._id === id);
    },
});

export const searchJobActivitiesState = atom<string | undefined>({
  key: "app.jobs.types.activities.search",
  default: undefined,
});

export const jobActivitiesPageState = atom({
  key: "app.jobs.types.activities.page",
  default: 1,
});

export const updateJobActivitiesState = atom<string | undefined>({
  key: "app.jobs.types.activities.update",
  default: undefined,
});

export const loadingJobActivitiesState = atom({
  key: "app.jobs.types.activities.loading",
  default: false,
});

export const editActivityState = atom<JobTypeActivityRequest>({
  key: "app.jobs.types.activities.edit",
  default: selector({
    key: "app.jobs.types.activities.editing.initializer",
    get: ({ get }) => {
      const selectedActivityId = get(selectedJobActivityIdState);
      if (!selectedActivityId) {
        return ACTIVITY_DEFAULTS;
      }
      const activity = get(jobActivityDetailState);
      const countActivities = get(jobActivitiesState)?.total || 0;
      return activity
        ? {
            ...activity,
            forms: activity.forms?.map((t) => t._id),
            nextActivities: activity.nextActivities?.map((t) => t._id),
            order: countActivities + 1,
          }
        : ACTIVITY_DEFAULTS;
    },
  }),
});

export const editActivityTouchedState = atom<JobActivityRequestTouched>({
  key: "app.jobs.types.activities.edit.touched",
  default: ACTIVITY_TOUCHED_DEFAULTS,
});

export const editActivityValidState = selector<{
  [key in keyof JobTypeActivityRequest]: boolean;
}>({
  key: "app.jobs.types.activities.edit.valid",
  get: ({ get }) => {
    const fields = get(editActivityState);

    // SET ALL FIELDS TO BE VALID
    const validFields = Object.fromEntries(
      Object.keys(fields).map((key) => [key, true])
    ) as {
      [key in keyof JobTypeActivityRequest]: boolean;
    };

    // VALIDATE REQUIRED FIELDS
    if (!fields.name) {
      validFields.name = false;
    }
    if (fields.order === undefined || fields.order === null) {
      validFields.order = false;
    }
    if (!fields.jobStatus) {
      validFields.jobStatus = false;
    }

    return validFields;
  },
});

export const editActivityErrorState = selector({
  key: "app.jobs.types.activities.edit.error",
  get: ({ get }) => {
    const validFields = get(editActivityValidState);
    return Object.values(validFields).some((valid) => !valid);
  },
});
